/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters*/
/* type parameters are necessary */
import { injectable, interfaces } from 'inversify';
import { ACDLError } from '../models/error.model';

export type EventListeningDisposer = () => void;

type Handler = (e: IACDLEvent) => void;

export interface IACDLEvent {
  get type(): symbol;
}

export interface IEventBus {
  addListener: <T extends IACDLEvent>(listener: (e: T) => void | Promise<void> | Promise<void | ACDLError>, type: symbol) => EventListeningDisposer;
  sendEvent: (e: IACDLEvent) => void;
}

export namespace IEventBus {
  export const $: interfaces.ServiceIdentifier<IEventBus> = Symbol('EVENT_BUS');
}

@injectable()
export class EventBus implements IEventBus {
  private observers: Map<symbol, Handler[]> = new Map<symbol, Handler[]>();

  addListener = <T extends IACDLEvent>(listener: (e: T) => void, type: symbol): EventListeningDisposer => {
    const observers = this.observers.get(type);
    if (!observers) {
      this.observers.set(type, [listener as Handler]);
    } else {
      observers.push(listener as (e: IACDLEvent) => void);
    }
    return this.generateDisposer(listener as Handler, type);
  };

  sendEvent = (e: IACDLEvent): void => {
    this.observers.get(e.type)?.forEach((l) => {
      l(e);
    });
  };

  private generateDisposer(listener: Handler, type: symbol): () => void {
    return () => {
      this.removeListener(listener, type);
    };
  }

  private removeListener(listener: Handler, type: symbol): void {
    const observers = this.observers.get(type);
    if (observers) {
      const indexOf = observers.indexOf(listener);
      if (indexOf > -1) {
        observers.splice(indexOf, 1);
      }
    }
  }
}
